import React, { useState, useEffect, useMemo } from "react";
import {
	Form,
	Button,
	DropdownButton,
	Dropdown,
	Card,
	Table,
} from "react-bootstrap";
import Modal from "../common/components/ThemedModal";
import {
	deleteSnapshot,
	getSnapshots,
	getUserTeamPreviewForEvent,
	replaceSnapshotForUserInSet,
	takeSnapshot,
} from "../../services/snapshotService";
import { getUsers } from "../../services/userService";
import {
	initialEvent,
	initialSeason,
	Season,
	Event,
	SnapshotSummary,
	Sport,
	Series,
	User,
} from "../../types/model";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getEventsThunk } from "../../store/thunks/fantasyThunk";
import AdminLayout from "./AdminLayout";

const AdminSnapshotForm = () => {
	const dispatch = useAppDispatch();
	const { events, seasons } = useAppSelector((state) => state.fantasy);

	const [selectedSeason, setSelectedSeason] = useState<Season>(initialSeason);
	const [selectedEvent, setSelectedEvent] = useState<Event>(initialEvent);
	const [selectedSportFilter, setSelectedSportFilter] = useState<string>("");
	const [selectedSeriesFilter, setSelectedSeriesFilter] = useState<string>("");
	const [snapshotName, setSnapshotName] = useState("");
	const [snapshots, setSnapshots] = useState<SnapshotSummary[]>([]);
	const [users, setUsers] = useState<User[]>([]);
	const [selectedSnapshotSetName, setSelectedSnapshotSetName] = useState("");
	const [selectedReplaceEventId, setSelectedReplaceEventId] = useState("");
	const [selectedUserId, setSelectedUserId] = useState("");
	const [userSearchTerm, setUserSearchTerm] = useState("");
	const [replaceStatusMessage, setReplaceStatusMessage] = useState("");
	const [replacementTeamPreview, setReplacementTeamPreview] = useState<any>(null);
	const [replacementTeamPreviewError, setReplacementTeamPreviewError] =
		useState("");
	const [replacementTeamPreviewLoading, setReplacementTeamPreviewLoading] =
		useState(false);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);

	const getId = (
		value?: string | { _id?: string } | null,
	): string | undefined => {
		if (!value) {
			return undefined;
		}
		if (typeof value === "string") {
			return value;
		}
		return value._id;
	};

	const getName = (
		value?: string | { name?: string } | null,
	): string | undefined => {
		if (!value) {
			return undefined;
		}
		if (typeof value === "string") {
			return value;
		}
		return value.name;
	};

	const getEntityName = (
		value?: string | { name?: string } | null,
		fallback?: string,
	): string => {
		if (!value || typeof value === "string") {
			return fallback || "";
		}
		return value.name || fallback || "";
	};

	const getEventDisplayLabel = (event: Event): string => {
		const sportName = getEntityName(event?.sport, "Unknown sport");
		const seriesName = getEntityName(event?.series, "Unknown series");
		return `${event?.name || "Unnamed event"} (${sportName} / ${seriesName})`;
	};

	const getModelName = (value?: any, fallback = "Unknown"): string => {
		if (!value) {
			return fallback;
		}
		if (typeof value === "string") {
			return value;
		}
		return value.name || fallback;
	};

	const seasonFilteredEvents = events.filter((event: Event) => {
		const eventSeasonId = getId(event?.season);
		if (!selectedSeason?._id) {
			return true;
		}
		return eventSeasonId === selectedSeason._id;
	});

	const sportsForSeason = seasonFilteredEvents.reduce<Sport[]>((acc, event) => {
		const sport = event?.sport;
		if (!sport || typeof sport === "string") {
			return acc;
		}
		if (!acc.some((s) => s._id === sport._id)) {
			acc.push(sport);
		}
		return acc;
	}, []);

	const seriesForSeasonAndSport = seasonFilteredEvents.reduce<Series[]>(
		(acc, event) => {
			const eventSportId = getId(event?.sport);
			if (selectedSportFilter && eventSportId !== selectedSportFilter) {
				return acc;
			}
			const series = event?.series;
			if (!series || typeof series === "string") {
				return acc;
			}
			if (!acc.some((s) => s._id === series._id)) {
				acc.push(series);
			}
			return acc;
		},
		[],
	);

	const filteredEvents = seasonFilteredEvents.filter((event: Event) => {
		const eventSportId = getId(event?.sport);
		const eventSeriesId = getId(event?.series);
		if (selectedSportFilter && eventSportId !== selectedSportFilter) {
			return false;
		}
		if (selectedSeriesFilter && eventSeriesId !== selectedSeriesFilter) {
			return false;
		}
		return true;
	});

	useEffect(() => {
		dispatch(getEventsThunk());
		fetchSnapshots();
		fetchUsers();
	}, [dispatch]);

	useEffect(() => {
		const fetchReplacementPreview = async () => {
			if (!selectedReplaceEventId || !selectedUserId) {
				setReplacementTeamPreview(null);
				setReplacementTeamPreviewError("");
				return;
			}

			setReplacementTeamPreviewLoading(true);
			setReplacementTeamPreviewError("");

			try {
				const res = await getUserTeamPreviewForEvent({
					eventId: selectedReplaceEventId,
					userId: selectedUserId,
				});
				setReplacementTeamPreview(res?.data || null);
			} catch (error: any) {
				setReplacementTeamPreview(null);
				setReplacementTeamPreviewError(
					String(
						error?.response?.data ||
							"Could not load this user's team for the selected event context.",
					),
				);
			} finally {
				setReplacementTeamPreviewLoading(false);
			}
		};

		fetchReplacementPreview();
	}, [selectedReplaceEventId, selectedUserId]);

	const fetchSnapshots = async () => {
		try {
			const res = await getSnapshots();
			setSnapshots(Array.isArray(res.data) ? res.data : []);
		} catch (error) {
			console.error("Error fetching snapshots:", error);
		}
	};

	const fetchUsers = async () => {
		try {
			const res = await getUsers();
			setUsers(Array.isArray(res.data) ? res.data : []);
		} catch (error) {
			console.error("Error fetching users:", error);
		}
	};

	const snapshotSetNames = useMemo(
		() => [...new Set(snapshots.map((snapshot) => snapshot.name).filter(Boolean))],
		[snapshots],
	);

	const filteredUsers = useMemo(() => {
		const normalizedSearch = userSearchTerm.trim().toLowerCase();
		if (!normalizedSearch) {
			return users;
		}

		return users.filter((user) => {
			const username = (user?.username || "").toLowerCase();
			const email = (user?.email || "").toLowerCase();
			return (
				username.includes(normalizedSearch) || email.includes(normalizedSearch)
			);
		});
	}, [users, userSearchTerm]);

	const selectedCreateEventFromList = events.find(
		(event: Event) => event?._id === selectedEvent?._id,
	);
	const selectedReplaceEventFromList = filteredEvents.find(
		(event: Event) => event?._id === selectedReplaceEventId,
	);

	const handleEventSelect = (eventId: string) => {
		const event = events.find((event) => event._id === eventId);
		if (!event) {
			alert("Event not found");
			return;
		}
		setSelectedEvent(event);
	};

	const handleSubmit = (e: any) => {
		e.preventDefault();
		setShowConfirmationModal(true);
	};

	const selectedEventSport = getName(selectedEvent?.sport) || "Unknown sport";
	const selectedEventSeries = getName(selectedEvent?.series) || "Unknown series";

	const handleConfirm = async () => {
		if (!selectedEvent?._id) {
			alert("Please select an event");
			setShowConfirmationModal(false);
			return;
		}

		const data = {
			eventId: selectedEvent._id,
			season: selectedSeason._id,
			name: snapshotName,
		};

		try {
			await takeSnapshot(data);
			await fetchSnapshots();
		} catch (error) {
			alert("Error taking snapshot");
			console.error("Error taking snapshot:", error);
		}

		setShowConfirmationModal(false);
	};

	const handleReplaceSnapshotForUser = async (e: React.FormEvent) => {
		e.preventDefault();
		setReplaceStatusMessage("");

		if (!selectedSnapshotSetName) {
			alert("Please select a snapshot set");
			return;
		}

		if (!selectedReplaceEventId) {
			alert("Please select an event");
			return;
		}

		if (!selectedUserId) {
			alert("Please select a user");
			return;
		}

		try {
			const res = await replaceSnapshotForUserInSet({
				snapshotName: selectedSnapshotSetName,
				eventId: selectedReplaceEventId,
				userId: selectedUserId,
			});

			const action = res?.data?.action === "created" ? "created" : "updated";
			const selectedUser = users.find((user) => user._id === selectedUserId);
			setReplaceStatusMessage(
				`Snapshot ${action} for ${selectedUser?.username || "selected user"}.`,
			);
			await fetchSnapshots();
		} catch (error: any) {
			const errorMessage =
				error?.response?.data ||
				"An error occurred while replacing snapshot for this user.";
			setReplaceStatusMessage(String(errorMessage));
			console.error("Error replacing snapshot for user in set:", error);
		}
	};

	const handleDelete = async (snapshot: SnapshotSummary) => {
		try {
			await deleteSnapshot(snapshot.name);
			await fetchSnapshots();
		} catch (error) {
			alert("Error deleting snapshot");
			console.error("Error deleting snapshot:", error);
		}
	};

	return (
		<AdminLayout>
			<div
				className="container py-4 px-4"
				style={{ minHeight: "100vh" }}
			>
			<h1 className="text-center">Snapshot Manager</h1>
			<p className="text-center">Make sure you know what you're doing. </p>
			<Card>
				<Card.Body>
					<Form onSubmit={handleSubmit}>
						<Form.Group controlId="eventSelect">
							<Form.Label>Select Event</Form.Label>
							<DropdownButton
								id="dropdown-basic-button"
								title={
									selectedCreateEventFromList
										? getEventDisplayLabel(selectedCreateEventFromList)
										: "Select an Event"
								}
							>
								{filteredEvents.map((event) => (
									<Dropdown.Item
										key={event._id}
										onClick={() => handleEventSelect(event?._id ?? "")}
									>
										{getEventDisplayLabel(event)}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Form.Group>
						<Form.Group controlId="seasonSelect" className="mt-3">
							<Form.Label>Select Season</Form.Label>
							<DropdownButton
								id="dropdown-basic-button"
								title={
									selectedSeason
										? seasons.find(
												(season: Season) => season?._id === selectedSeason?._id,
										  )?.name
										: "Select a Season"
								}
							>
								{seasons.map((season) => (
									<Dropdown.Item
										key={season._id}
										onClick={() => {
											setSelectedSeason(season);
											setSelectedSportFilter("");
											setSelectedSeriesFilter("");
											setSelectedEvent(initialEvent);
										}}
									>
										{season.name}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Form.Group>
						<Form.Group controlId="sportFilterSelect" className="mt-3">
							<Form.Label>Filter by Sport</Form.Label>
							<DropdownButton
								id="dropdown-sport-filter-button"
								title={
									selectedSportFilter
										? sportsForSeason.find(
												(sport: Sport) => sport?._id === selectedSportFilter,
										  )?.name || "Select a Sport"
										: "All Sports"
								}
							>
								<Dropdown.Item
									onClick={() => {
										setSelectedSportFilter("");
										setSelectedSeriesFilter("");
										setSelectedEvent(initialEvent);
									}}
								>
									All Sports
								</Dropdown.Item>
								{sportsForSeason.map((sport) => (
									<Dropdown.Item
										key={sport._id}
										onClick={() => {
											setSelectedSportFilter(sport._id);
											setSelectedSeriesFilter("");
											setSelectedEvent(initialEvent);
										}}
									>
										{sport.name}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Form.Group>
						<Form.Group controlId="seriesFilterSelect" className="mt-3">
							<Form.Label>Filter by Series</Form.Label>
							<DropdownButton
								id="dropdown-series-filter-button"
								title={
									selectedSeriesFilter
										? seriesForSeasonAndSport.find(
												(series: Series) => series?._id === selectedSeriesFilter,
										  )?.name || "Select a Series"
										: "All Series"
								}
							>
								<Dropdown.Item
									onClick={() => {
										setSelectedSeriesFilter("");
										setSelectedEvent(initialEvent);
									}}
								>
									All Series
								</Dropdown.Item>
								{seriesForSeasonAndSport.map((series) => (
									<Dropdown.Item
										key={series._id}
										onClick={() => {
											setSelectedSeriesFilter(series._id);
											setSelectedEvent(initialEvent);
										}}
									>
										{series.name}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Form.Group>
						<Form.Group controlId="snapshotName" className="mt-3">
							<Form.Label>Snapshot Name</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter snapshot name (Anything unique, to be able to track the snapshot)"
								value={snapshotName}
								onChange={(e) => setSnapshotName(e.target.value)}
							/>
						</Form.Group>
						<Button variant="primary" type="submit" className="mt-2">
							Take Snapshot
						</Button>
					</Form>
				</Card.Body>
			</Card>
			<Card className="mt-3">
				<Card.Body>
					<Card.Title>Replace User Snapshot In Set</Card.Title>
					<Form onSubmit={handleReplaceSnapshotForUser}>
						<Form.Group controlId="snapshotSetSelect">
							<Form.Label>Snapshot Set</Form.Label>
							<Form.Select
								value={selectedSnapshotSetName}
								onChange={(e) => setSelectedSnapshotSetName(e.target.value)}
							>
								<option value="">Select snapshot set</option>
								{snapshotSetNames.map((name) => (
									<option key={name} value={name}>
										{name}
									</option>
								))}
							</Form.Select>
						</Form.Group>

						<Form.Group controlId="replaceEventSelect" className="mt-3">
							<Form.Label>Event</Form.Label>
							<DropdownButton
								id="dropdown-replace-event-button"
								title={
									selectedReplaceEventFromList
										? getEventDisplayLabel(selectedReplaceEventFromList)
										: "Select an Event"
								}
							>
								{filteredEvents.map((event) => (
									<Dropdown.Item
										key={event._id}
										onClick={() => setSelectedReplaceEventId(event?._id ?? "")}
									>
										{getEventDisplayLabel(event)}
									</Dropdown.Item>
								))}
							</DropdownButton>
						</Form.Group>

						<Form.Group controlId="replaceUserSearch" className="mt-3">
							<Form.Label>Find User</Form.Label>
							<Form.Control
								type="text"
								placeholder="Search by username or email"
								value={userSearchTerm}
								onChange={(e) => setUserSearchTerm(e.target.value)}
							/>
						</Form.Group>

						<Form.Group controlId="replaceUserSelect" className="mt-3">
							<Form.Label>User</Form.Label>
							<Form.Select
								value={selectedUserId}
								onChange={(e) => setSelectedUserId(e.target.value)}
							>
								<option value="">Select user</option>
								{filteredUsers.map((user) => (
									<option key={user._id} value={user._id}>
										{user.username} ({user.email})
									</option>
								))}
							</Form.Select>
						</Form.Group>

						{replacementTeamPreviewLoading && (
							<div className="mt-3">
								<small>Loading team preview...</small>
							</div>
						)}
						{replacementTeamPreviewError && (
							<div className="mt-3">
								<small>{replacementTeamPreviewError}</small>
							</div>
						)}
						{replacementTeamPreview && (
							<div className="mt-3 p-3 border rounded">
								<div>
									<strong>User Teams</strong>
								</div>
								<div className="mb-2">
									{replacementTeamPreview?.user?.username} (
									{replacementTeamPreview?.user?.email})
								</div>
								{(replacementTeamPreview?.allTeams || []).length > 0 ? (
									<ul className="mb-2">
										{replacementTeamPreview.allTeams.map((team: any) => (
											<li key={team?._id}>
												{getModelName(team?.season)} / {getModelName(team?.series)} /{" "}
												{getModelName(team?.sport)}
												{team?.season?.isActive ? " (Active Season)" : ""}
											</li>
										))}
									</ul>
								) : (
									<div>No teams found for this user.</div>
								)}

								{replacementTeamPreview?.team ? (
									<>
										<div className="mt-2">
											<strong>Current Team (Replacement Source)</strong>
										</div>
										<div>Team ID: {replacementTeamPreview?.team?._id}</div>
										<div>
											Selected Brand Team:{" "}
											{replacementTeamPreview?.selectedTeamLabel ||
												replacementTeamPreview?.team?.selectedTeam ||
												"None"}
										</div>
										<div>
											Best Trick Men:{" "}
											{replacementTeamPreview?.team?.bestTrickRiderMen
												? `${replacementTeamPreview?.team?.bestTrickRiderMen?.first_name || ""} ${replacementTeamPreview?.team?.bestTrickRiderMen?.last_name || ""}`.trim()
												: "None"}
										</div>
										<div>
											People's Choice Men:{" "}
											{replacementTeamPreview?.team?.PeoplesChoiceRiderMen
												? `${replacementTeamPreview?.team?.PeoplesChoiceRiderMen?.first_name || ""} ${replacementTeamPreview?.team?.PeoplesChoiceRiderMen?.last_name || ""}`.trim()
												: "None"}
										</div>
										<div>
											Best Trick Women:{" "}
											{replacementTeamPreview?.team?.bestTrickRiderWomen
												? `${replacementTeamPreview?.team?.bestTrickRiderWomen?.first_name || ""} ${replacementTeamPreview?.team?.bestTrickRiderWomen?.last_name || ""}`.trim()
												: "None"}
										</div>
										<div>
											People's Choice Women:{" "}
											{replacementTeamPreview?.team?.PeoplesChoiceRiderWomen
												? `${replacementTeamPreview?.team?.PeoplesChoiceRiderWomen?.first_name || ""} ${replacementTeamPreview?.team?.PeoplesChoiceRiderWomen?.last_name || ""}`.trim()
												: "None"}
										</div>
										<div className="mt-2">
											<strong>Riders</strong>
										</div>
										<ul className="mb-0">
											{(replacementTeamPreview?.team?.riders || []).map(
												(teamRider: any, index: number) => (
													<li
														key={`${teamRider?.rider?._id || "rider"}-${index}`}
													>
														#{teamRider?.rank}{" "}
														{`${teamRider?.rider?.first_name || ""} ${teamRider?.rider?.last_name || ""}`.trim()}
													</li>
												),
											)}
										</ul>
									</>
								) : (
									<div className="mt-2">
										No team found for this user in the selected event context.
									</div>
								)}
							</div>
						)}

						<Button variant="primary" type="submit" className="mt-3">
							Replace User Snapshot In Set
						</Button>
					</Form>
					{replaceStatusMessage && (
						<div className="mt-3">
							<small>{replaceStatusMessage}</small>
						</div>
					)}
				</Card.Body>
			</Card>
			<Card className="mt-3">
				<Card.Body>
					<Card.Title>Snapshots</Card.Title>
					<Table striped bordered hover>
						<thead>
							<tr>
								<th>Name</th>
								<th>Sport</th>
								<th>Series</th>
								<th>Date Created</th>
								<th>Snapshots</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{snapshots.map((snapshot) => (
								<tr key={snapshot?._id}>
									<td>{snapshot?.name}</td>
									<td>{snapshot?.sport?.name}</td>
									<td>{snapshot?.series?.name}</td>
									<td>
										{snapshot?.dateCreated
											? typeof snapshot.dateCreated === "string"
												? snapshot.dateCreated
												: snapshot.dateCreated.toLocaleString()
											: ""}
									</td>
									<td>{snapshot?.snapshotCount ?? 0}</td>
									<td>
										<Button
											variant="outline-primary"
											onClick={() => handleDelete(snapshot)}
										>
											Delete
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
			<Modal
				show={showConfirmationModal}
				onHide={() => setShowConfirmationModal(false)}
			>
				<Modal.Header closeButton>
					<Modal.Title>Confirm Snapshot</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						Are you sure you want to take a snapshot for event{" "}
						<strong>{selectedEvent?.name || "Unknown event"}</strong> (Sport:{" "}
						<strong>{selectedEventSport}</strong>, Series:{" "}
						<strong>{selectedEventSeries}</strong>) with name{" "}
						<strong>{snapshotName}</strong>?
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => setShowConfirmationModal(false)}
					>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleConfirm}>
						Yes, Take Snapshot
					</Button>
				</Modal.Footer>
			</Modal>
			</div>
		</AdminLayout>
	);
};

export default AdminSnapshotForm;
